/**
 * Hash-based routing.
 *
 * Hash routing rather than the History API because this is a static site on
 * GitHub Pages: there is no server to rewrite deep paths, so /d/<code> would
 * 404 on a hard refresh or a shared link. #/d/<code> always resolves to
 * index.html, which is exactly the property "shareable by URL alone" needs.
 */
import { useEffect, useState } from 'react';

export type Route =
  | { name: 'describe' }
  | { name: 'options'; brief: string; seed: string }
  | { name: 'design'; code: string }
  | { name: 'knot'; code: string }
  | { name: 'print'; code: string }
  | { name: 'settings' }
  | { name: 'help' };

export function parseHash(hash: string): Route {
  const raw = hash.replace(/^#/, '').replace(/^\//, '');
  const [path, query] = raw.split('?');
  const parts = (path ?? '').split('/').filter(Boolean);
  const params = new URLSearchParams(query ?? '');

  if (parts[0] === 'd' && parts[1]) {
    if (parts[2] === 'knot') return { name: 'knot', code: parts[1] };
    if (parts[2] === 'print') return { name: 'print', code: parts[1] };
    return { name: 'design', code: parts[1] };
  }
  if (parts[0] === 'options') {
    return { name: 'options', brief: params.get('b') ?? '', seed: params.get('s') ?? '' };
  }
  if (parts[0] === 'settings') return { name: 'settings' };
  if (parts[0] === 'help') return { name: 'help' };
  return { name: 'describe' };
}

export function hrefFor(route: Route): string {
  switch (route.name) {
    case 'describe':
      return '#/';
    case 'options':
      return `#/options?b=${encodeURIComponent(route.brief)}&s=${encodeURIComponent(route.seed)}`;
    case 'design':
      return `#/d/${route.code}`;
    case 'knot':
      return `#/d/${route.code}/knot`;
    case 'print':
      return `#/d/${route.code}/print`;
    case 'settings':
      return '#/settings';
    case 'help':
      return '#/help';
  }
}

export function navigate(route: Route): void {
  window.location.hash = hrefFor(route).slice(1);
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));
  useEffect(() => {
    const onChange = () => {
      setRoute(parseHash(window.location.hash));
      window.scrollTo(0, 0);
    };
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}
